// =============================================================================
// BP-07-03: Resources Frontend - Types
// =============================================================================

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export type ResourceType = 
  | 'article' 
  | 'guide' 
  | 'video' 
  | 'checklist' 
  | 'template' 
  | 'external';

export type LifeStage = 
  | 'crisis' 
  | 'early_intervention' 
  | 'school_age' 
  | 'transition' 
  | 'adult';

export type ResourceStatus = 'draft' | 'published' | 'archived';

// -----------------------------------------------------------------------------
// Core Interfaces
// -----------------------------------------------------------------------------

export interface ResourceCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  displayOrder: number;
  parentId: string | null;
  children?: ResourceCategory[];
  resourceCount?: number;
}

export interface Resource {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  categoryId: string | null;
  category?: ResourceCategory;
  resourceType: ResourceType;
  featuredImageUrl: string | null;
  videoUrl: string | null;
  attachments: ResourceAttachment[];
  diagnosisIds: string[];
  diagnoses?: DiagnosisSummary[];
  ageRangeMin: number | null;
  ageRangeMax: number | null;
  lifeStage: LifeStage | null;
  tags: string[];
  metaTitle: string | null;
  metaDescription: string | null;
  viewCount: number;
  saveCount: number;
  avgRating: number | null;
  ratingCount: number;
  authorId: string | null;
  authorName: string | null;
  status: ResourceStatus;
  publishedAt: string | null;
  isPremium: boolean;
  readingTimeMinutes: number | null;
  createdAt: string;
  updatedAt: string;
  isSaved?: boolean;
  userRating?: number | null;
}

export interface ResourceAttachment {
  name: string;
  url: string;
  type: string;
  size?: number;
}

export interface DiagnosisSummary {
  id: string;
  slug: string;
  name: string;
  shortName: string | null;
}

export interface Diagnosis {
  id: string;
  slug: string;
  name: string;
  shortName: string | null;
  description: string | null;
  overview: string | null;
  causes: string | null;
  symptoms: string | null;
  treatmentOptions: string | null;
  prognosis: string | null;
  iconUrl: string | null;
  heroImageUrl: string | null;
  icd10Codes: string[];
  relatedDiagnoses: string[];
  relatedDiagnosisDetails?: DiagnosisSummary[];
  resourceCount: number;
  specialistTypes: string[];
  isActive: boolean;
  displayOrder: number;
  seoTitle: string | null;
  seoDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SavedResource {
  id: string;
  userId: string;
  resourceId: string;
  resource?: Resource;
  notes: string | null;
  folder: string | null;
  createdAt: string;
}

export interface ResourceRating {
  resourceId: string;
  userId: string;
  rating: number;
  createdAt: string;
}

// -----------------------------------------------------------------------------
// Request/Response Interfaces
// -----------------------------------------------------------------------------

export interface ResourceFilters {
  categorySlug?: string;
  diagnosisId?: string;
  diagnosisSlug?: string;
  resourceType?: ResourceType;
  lifeStage?: LifeStage;
  ageMin?: number;
  ageMax?: number;
  tags?: string[];
  isPremium?: boolean;
  search?: string;
}

export interface ResourceSearchParams {
  q: string;
  categorySlug?: string;
  diagnosisSlug?: string;
  resourceType?: ResourceType;
  lifeStage?: LifeStage;
  page?: number;
  limit?: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface SaveResourceRequest {
  notes?: string;
  folder?: string;
}

export interface RateResourceRequest {
  rating: number; // 1-5
}

// -----------------------------------------------------------------------------
// UI State Interfaces
// -----------------------------------------------------------------------------

export interface ResourceFilterState {
  categorySlug: string | null;
  diagnosisSlug: string | null;
  resourceType: ResourceType | null;
  lifeStage: LifeStage | null;
  ageRange: [number, number] | null;
  tags: string[];
  searchQuery: string;
}

export interface ResourceViewState {
  viewMode: 'grid' | 'list';
  sortBy: 'newest' | 'popular' | 'rating' | 'title';
  recentlyViewed: string[]; // Resource IDs
  readingProgress: Record<string, number>; // resourceId -> percentage
}

// -----------------------------------------------------------------------------
// Diagnosis Hub Sections
// -----------------------------------------------------------------------------

export interface DiagnosisSection {
  id: string;
  title: string;
  anchor: string;
  content: string | null;
}

export const DIAGNOSIS_SECTIONS: DiagnosisSection[] = [
  { id: 'overview', title: 'Overview', anchor: 'overview', content: null },
  { id: 'causes', title: 'Causes', anchor: 'causes', content: null },
  { id: 'symptoms', title: 'Symptoms', anchor: 'symptoms', content: null },
  { id: 'treatment', title: 'Treatment Options', anchor: 'treatment', content: null },
  { id: 'prognosis', title: 'Prognosis', anchor: 'prognosis', content: null },
  { id: 'resources', title: 'Resources', anchor: 'resources', content: null },
  { id: 'specialists', title: 'Specialists', anchor: 'specialists', content: null },
];

// -----------------------------------------------------------------------------
// Resource Type Display Helpers
// -----------------------------------------------------------------------------

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  article: 'Article',
  guide: 'Guide',
  video: 'Video',
  checklist: 'Checklist',
  template: 'Template',
  external: 'External Link',
};

export const RESOURCE_TYPE_ICONS: Record<ResourceType, string> = {
  article: 'FileText',
  guide: 'BookOpen',
  video: 'PlayCircle',
  checklist: 'CheckSquare',
  template: 'FileCode',
  external: 'ExternalLink',
};

export const LIFE_STAGE_LABELS: Record<LifeStage, string> = {
  crisis: 'Crisis / Initial Diagnosis',
  early_intervention: 'Early Intervention (0-3)',
  school_age: 'School Age (3-18)',
  transition: 'Transition to Adulthood',
  adult: 'Adult Care',
};

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const resourceQueryKeys = {
  all: ['resources'] as const,
  lists: () => [...resourceQueryKeys.all, 'list'] as const,
  list: (filters: ResourceFilters, pagination: PaginationParams) => 
    [...resourceQueryKeys.lists(), { filters, pagination }] as const,
  details: () => [...resourceQueryKeys.all, 'detail'] as const,
  detail: (slug: string) => [...resourceQueryKeys.details(), slug] as const,
  categories: ['resource-categories'] as const,
  category: (slug: string) => [...resourceQueryKeys.categories, slug] as const,
  search: (params: ResourceSearchParams) => [...resourceQueryKeys.all, 'search', params] as const,
  featured: () => [...resourceQueryKeys.all, 'featured'] as const,
  popular: () => [...resourceQueryKeys.all, 'popular'] as const,
  saved: () => [...resourceQueryKeys.all, 'saved'] as const,
};

export const diagnosisQueryKeys = {
  all: ['diagnoses'] as const,
  lists: () => [...diagnosisQueryKeys.all, 'list'] as const,
  details: () => [...diagnosisQueryKeys.all, 'detail'] as const,
  detail: (slug: string) => [...diagnosisQueryKeys.details(), slug] as const,
  resources: (slug: string) => [...diagnosisQueryKeys.detail(slug), 'resources'] as const,
};

// Resource Type Labels
export const resourceTypeLabels: Record<string, string> = {
  article: 'Article',
  guide: 'Guide',
  video: 'Video',
  checklist: 'Checklist',
  template: 'Template',
  tool: 'Tool',
  webinar: 'Webinar',
  podcast: 'Podcast',
  ebook: 'E-Book',
  infographic: 'Infographic',
};

// Life Stage Labels
export const lifeStageLabels: Record<string, string> = {
  'crisis-diagnosis': 'Crisis & Diagnosis',
  'early-intervention': 'Early Intervention',
  'school-age': 'School Age',
  'adolescence': 'Adolescence',
  'transition': 'Transition to Adulthood',
  'adult': 'Adult',
  'aging': 'Aging & End of Life',
};
